import React, { useState } from "react";
import hrApi from "./hrApi";
import { splitName } from "./NameInputs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MessageCircleQuestion, UserPlus, Mail, FolderInput, Sparkles, Loader2, Link2, X, Reply, Plus, Briefcase, AtSign, Paperclip, AlertTriangle, Bot, ListChecks, CheckSquare } from "lucide-react";

export const mailto = (r) => `mailto:${encodeURIComponent(r.to)}?subject=${encodeURIComponent(r.subject)}&body=${encodeURIComponent(r.body)}`;

export function ReplyInMail({ reply, testid }) {
  if (!reply) return null;
  return (
    <a href={mailto(reply)} data-testid={testid} className="inline-flex items-center gap-1.5 rounded-2xl h-12 px-4 border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50">
      <Reply size={15} /> Reply in Mail
    </a>
  );
}

const CHIPS = [["start_files", "Start new employee files", UserPlus], ["log_correspondence", "Log as correspondence", Mail], ["file_existing", "File to existing employee", FolderInput], ["log_warning", "Log as a warning", AlertTriangle]];

const MODES = [["review", "I approve each file", "Nothing is filed or sent until you press Do all."],
  ["auto_file", "Auto-file, ask before sending", "When it's sure, it files and logs on its own. SMS and emails wait in Ready to send."],
  ["auto_all", "Fully automatic when sure", "When it's sure, it does everything, including SMS and emails. Reply drafts still wait in Ready to send."]];

export function AssistantMode({ tick }) {
  const [s, setS] = useState(null);
  const load = () => hrApi.get("/assistant").then((r) => setS(r.data)).catch(() => {});
  React.useEffect(() => { load(); }, [tick]);
  const set = async (mode) => { try { await hrApi.put("/assistant", { mode }); toast.success("Assistant mode updated"); load(); } catch { toast.error("Could not change mode"); } };
  if (!s) return null;
  return (
    <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-3 flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="assistant-mode">
      <span className="text-sm font-bold text-slate-800 inline-flex items-center gap-1.5"><Bot size={17} className="text-indigo-500" /> HR assistant</span>
      <div className="flex flex-wrap gap-1 bg-slate-100 rounded-full p-1">{MODES.map(([k, l]) => (
        <button key={k} data-testid={`assistant-mode-${k}`} onClick={() => set(k)} className={`px-3 h-8 rounded-full text-xs font-semibold transition-colors ${s.mode === k ? "bg-indigo-600 text-white shadow" : "text-slate-600 hover:bg-white"}`}>{l}</button>))}</div>
      <span className="text-xs text-slate-500" data-testid="assistant-mode-help">{MODES.find((m) => m[0] === s.mode)?.[2]}</span>
      {s.auto_recent > 0 && <span className="text-xs font-semibold text-emerald-700 sm:ml-auto" data-testid="assistant-auto-count">{s.auto_recent} file(s) done automatically in the last 24h (see History)</span>}
    </div>
  );
}

export function StepList({ c, p, save }) {
  const skip = c.inputs.skip || [];
  const toggle = (k, on) => save({ skip: on ? skip.filter((x) => x !== k) : [...skip, k] });
  return (
    <div>
      <div className="text-xs font-semibold text-slate-500 mb-1 inline-flex items-center gap-1"><ListChecks size={13} /> Do all will</div>
      <ol className="space-y-1.5" data-testid={`cf-actions-${c.id}`}>
        {p.actions.map((a, i) => <li key={i} className="text-xs text-slate-700 flex gap-2 items-start"><CheckSquare size={15} className="text-emerald-500 shrink-0" />{a}</li>)}
        {(p.steps || []).map((s) => (
          <li key={s.key}><label className="text-xs text-slate-700 flex gap-2 items-start cursor-pointer">
            <input type="checkbox" checked={s.on} onChange={(e) => toggle(s.key, e.target.checked)} className="w-[15px] h-[15px] accent-emerald-500 mt-px shrink-0" data-testid={`cf-step-${s.key}-${c.id}`} />
            <span className={s.on ? "" : "line-through text-slate-400"}>{s.text}</span></label></li>))}
      </ol>
      {(p.steps || []).length > 0 && <p className="text-[11px] text-slate-400 mt-1">Untick anything you don't want done.</p>}
    </div>
  );
}

export function AskBack({ c, onChanged }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (payload) => {
    setBusy(true);
    try { await hrApi.post(`/confirmations/${c.id}/instruct`, payload); toast.success("Re-reading with your instruction…"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not re-read"); }
    setBusy(false);
  };
  return (
    <div className="m-3 rounded-2xl border border-indigo-200 bg-indigo-50/60 p-4 space-y-3" data-testid={`cf-askback-${c.id}`}>
      <div className="font-heading font-bold text-slate-800 inline-flex items-center gap-2"><MessageCircleQuestion size={18} className="text-indigo-500" /> What would you like to do with this?</div>
      <div className="flex flex-wrap gap-2">
        {CHIPS.map(([k, l, Icon]) => (
          <button key={k} disabled={busy} data-testid={`cf-chip-${k}-${c.id}`} onClick={() => go({ action: k })}
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-indigo-200 px-4 h-10 text-sm font-semibold text-indigo-700 hover:bg-indigo-500 hover:text-white transition-colors"><Icon size={15} /> {l}</button>
        ))}
      </div>
      <div className="flex gap-2">
        <Input data-testid={`cf-instruct-input-${c.id}`} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && text.trim() && go({ instruction: text })}
          placeholder="Or tell me in plain English, e.g. “start new files for these employees”" className="h-11 bg-white" />
        <Button data-testid={`cf-instruct-go-${c.id}`} disabled={busy || !text.trim()} onClick={() => go({ instruction: text })} className="h-11 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white gap-1">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Do it</Button>
      </div>
      <p className="text-[11px] text-slate-500">My best guess is shown below — you can also confirm it as is.</p>
    </div>
  );
}

export function CaseAttach({ c, save }) {
  const cc = c.inputs?.case_candidates || [];
  if (!cc.length || !["medical_certificate", "incident_report", "employee_document", "correspondence"].includes(c.inputs.kind)) return null;
  return (
    <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-2.5" data-testid={`cf-case-${c.id}`}>
      <div className="text-xs font-semibold text-rose-700 inline-flex items-center gap-1 mb-1"><Briefcase size={12} /> Attach to case</div>
      <Select value={c.inputs.case_id || "none"} onValueChange={(v) => save({ case_id: v === "none" ? null : v })}>
        <SelectTrigger className="h-8 text-xs bg-white" data-testid={`cf-case-select-${c.id}`}><SelectValue /></SelectTrigger>
        <SelectContent>{cc.map((x) => <SelectItem key={x.id} value={x.id}>{x.label} — {x.reason}</SelectItem>)}
          <SelectItem value="none">{c.inputs.kind === "incident_report" ? "Don't attach — create a NEW case" : "Don't attach to a case"}</SelectItem></SelectContent>
      </Select>
    </div>
  );
}

export function groupInbound(items) {
  const out = [], idx = {};
  items.forEach((c) => {
    const g = c.inbound?.group_id;
    if (!g) return out.push({ key: c.id, items: [c] });
    if (idx[g] === undefined) { idx[g] = out.length; out.push({ key: g, inbound: c.inbound, items: [] }); }
    out[idx[g]].items.push(c);
  });
  out.forEach((g) => g.items.sort((a, b) => (a.inbound?.role === "body" ? -1 : b.inbound?.role === "body" ? 1 : 0)));
  return out;
}

export function InboundHeader({ g }) {
  return (
    <div className="flex items-center gap-2 px-1 pt-2" data-testid={`cf-group-${g.key}`}>
      <span className="w-8 h-8 rounded-xl bg-slate-900 text-white grid place-items-center"><AtSign size={15} /></span>
      <div className="min-w-0"><div className="text-sm font-bold text-slate-800 truncate">{g.inbound.subject}</div>
        <div className="text-xs text-slate-500">Forwarded by {g.inbound.from} · <Paperclip size={11} className="inline" /> {g.inbound.attachment_count} attachment(s) · each read separately</div></div>
    </div>
  );
}

export function InboundSetup() {
  const [s, setS] = useState(null);
  React.useEffect(() => { hrApi.get("/inbound/status").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s?.configured) return null;
  return (
    <div className="rounded-2xl bg-slate-900 text-slate-200 p-3 text-xs flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="inbound-setup">
      <span className="inline-flex items-center gap-1.5 font-semibold text-white"><Mail size={13} /> Forward emails to <span className="font-mono text-emerald-300" data-testid="inbound-address">{s.address}</span></span>
      <span>Accepted from: {s.allowed.join(", ")}</span>
      {s.last && <span className="text-slate-400">Last: {s.last.subject} ({s.last.status})</span>}
    </div>
  );
}

export function EmailHeaders({ c }) {
  const e = c.inputs?.email || {};
  if (!e.from && !e.subject) return null;
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-100 p-2.5 text-xs space-y-0.5" data-testid={`cf-email-${c.id}`}>
      {[["From", e.from], ["To", e.to], ["Cc", e.cc], ["Date", e.date], ["Subject", e.subject]].filter(([, v]) => v).map(([k, v]) => (
        <div key={k} className="flex gap-2"><span className="w-14 text-slate-400 shrink-0">{k}</span><span className="text-slate-700 font-medium break-all">{v}</span></div>))}
      {c.ai?.ocr?.pages > 0 && <div className="text-[10px] text-slate-400 pt-1">Read {c.ai.ocr.pages} page(s) + {c.ai.ocr.images} embedded image(s) with OCR</div>}
    </div>
  );
}

function PersonRow({ c, p, i, emps, set }) {
  const linkName = emps.find((e) => e.id === p.link_employee_id)?.name;
  return (
    <div data-testid={`cf-person-${c.id}-${i}`} className={`border rounded-xl p-2 space-y-1.5 ${p.include === false ? "opacity-50" : p.link_employee_id ? "border-sky-200 bg-sky-50/40" : "border-emerald-200 bg-emerald-50/30"}`}>
      <div className="flex items-center gap-2">
        <input type="checkbox" className="w-4 h-4 accent-indigo-600" checked={p.include !== false} onChange={(e) => set({ include: e.target.checked })} data-testid={`cf-person-include-${c.id}-${i}`} />
        <Input value={p.first_name ?? splitName(p.name).first} placeholder="First name" onChange={(e) => set({ first_name: e.target.value, family_name: p.family_name ?? splitName(p.name).last, name: `${e.target.value} ${p.family_name ?? splitName(p.name).last}`.trim() }, true)} onBlur={() => set({}, false)} className="h-8 text-xs font-semibold flex-1" data-testid={`cf-person-first-${c.id}-${i}`} />
        <Input value={p.family_name ?? splitName(p.name).last} placeholder="Last name" onChange={(e) => set({ family_name: e.target.value, first_name: p.first_name ?? splitName(p.name).first, name: `${p.first_name ?? splitName(p.name).first} ${e.target.value}`.trim() }, true)} onBlur={() => set({}, false)} className="h-8 text-xs font-semibold flex-1 uppercase" data-testid={`cf-person-last-${c.id}-${i}`} />
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${p.link_employee_id ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700"}`}>{p.link_employee_id ? "Link existing" : "New profile"}</span>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {[["mobile", "Mobile"], ["role", "Role"], ["start_date", "Start (YYYY-MM-DD)"]].map(([k, l]) => (
          <Input key={k} placeholder={l} value={p[k] || ""} onChange={(e) => set({ [k]: e.target.value }, true)} onBlur={() => set({}, false)} className="h-8 text-xs" data-testid={`cf-person-${k}-${c.id}-${i}`} />))}
      </div>
      <Select value={p.link_employee_id || "new"} onValueChange={(v) => set({ link_employee_id: v === "new" ? null : v, match_reason: v === "new" ? "" : "chosen by HR" })}>
        <SelectTrigger className="h-8 text-xs" data-testid={`cf-person-link-${c.id}-${i}`}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="new">Create a new profile</SelectItem>
          {(p.candidates || []).map((x) => <SelectItem key={x.employee_id} value={x.employee_id}>Link to {x.employee_name} · {x.score}%</SelectItem>)}
          {p.link_employee_id && !(p.candidates || []).some((x) => x.employee_id === p.link_employee_id) && <SelectItem value={p.link_employee_id}>Link to {linkName || "existing"}</SelectItem>}
        </SelectContent></Select>
      {p.match_reason && <div className="text-[11px] text-sky-700 inline-flex items-center gap-1"><Link2 size={11} /> Possible duplicate — {p.match_reason}</div>}
    </div>
  );
}

export function PeopleEditor({ c, emps, save }) {
  const [rows, setRows] = useState(c.inputs.people || []);
  React.useEffect(() => { setRows(c.inputs.people || []); }, [c.id, c.inputs.people?.length, c.inputs.instruction]);
  const set = (i) => (p, local) => {
    const next = rows.map((r, j) => (j === i ? { ...r, ...p } : r)); setRows(next);
    if (!local) save({ people: next });
  };
  const add = () => { const next = [...rows, { idx: rows.length, name: "New person", include: true, candidates: [] }]; setRows(next); save({ people: next }); };
  return (
    <div className="space-y-1.5" data-testid={`cf-people-${c.id}`}>
      <div className="text-xs font-semibold text-slate-500">{rows.length} person(s) found</div>
      {rows.map((p, i) => <PersonRow key={i} c={c} p={p} i={i} emps={emps} set={set(i)} />)}
      <button onClick={add} data-testid={`cf-person-add-${c.id}`} className="text-xs text-indigo-600 inline-flex items-center gap-1"><Plus size={12} /> Add person</button>
    </div>
  );
}

export function CommLinks({ c, emps, save }) {
  const ids = c.inputs.comm_employee_ids || [];
  const set = (next) => save({ comm_employee_ids: next });
  return (
    <div data-testid={`cf-comm-links-${c.id}`}>
      <div className="text-xs font-semibold text-slate-500 mb-1">Link to employees mentioned</div>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {ids.map((id) => <span key={id} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold px-2.5 py-1">{emps.find((e) => e.id === id)?.name || "…"}
          <button onClick={() => set(ids.filter((x) => x !== id))} data-testid={`cf-comm-remove-${id}`}><X size={12} /></button></span>)}
        {ids.length === 0 && <span className="text-xs text-slate-400">No one linked yet</span>}
      </div>
      <Select value="" onValueChange={(v) => set([...ids, v])}>
        <SelectTrigger className="h-8 text-xs" data-testid={`cf-comm-add-${c.id}`}><SelectValue placeholder="Add employee…" /></SelectTrigger>
        <SelectContent>{emps.filter((e) => !ids.includes(e.id)).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
